import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./kaispecialrounds.scss";
import RoundStats from "./RoundStats";
import RoundTransactions from "./RoundTransactions";
import { CalendarIcon } from "./RoundIcons";
import { ROUNDS } from "./staticData";
import apiClient from "../../../utils/apiClient.util";
import useWeb3 from "../../../hooks/useWeb3";
import { getPreSaleContract } from "../../../utils/contractHelpers";

const TIME_FILTERS = ["1H", "1D", "1W", "1M", "1Y"];

const getDateRange = (filter) => {

  const now = new Date();
  if (filter === "1H") return { startDate: new Date(now - 60 * 60 * 1000), endDate: now };
  if (filter === "1D") return { startDate: new Date(now - 24 * 60 * 60 * 1000), endDate: now };
  if (filter === "1W") return { startDate: new Date(now - 7 * 24 * 60 * 60 * 1000), endDate: now };
  if (filter === "1M") {
    const s = new Date(now); s.setMonth(s.getMonth() - 1);
    return { startDate: s, endDate: now };
  }
  if (filter === "1Y") {
    const s = new Date(now); s.setFullYear(s.getFullYear() - 1);
    return { startDate: s, endDate: now };
  }
  return {};
};

const KaiSpecialRounds = () => {
  const [activeRound, setActiveRound] = useState(ROUNDS[0].key);
  const [activeFilter, setActiveFilter] = useState("1H");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showcalendar, setShowCalendar] = useState(false);
  const [dateRangeState, setDateRangeState] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [appliedDates, setAppliedDates] = useState(null);
  const [chainRounds, setChainRounds] = useState([]);
  const [liveRound, setLiveRound] = useState(null);
  const [roundLoading, setRoundLoading] = useState(true);

  const activeRoundMeta =
    ROUNDS.find((r) => r.key === activeRound) || ROUNDS[0];
  const privateSale = activeRoundMeta.privateSale;
  const roundContract = activeRoundMeta.contractAddress;

  const calendarRef = useRef(null);
  const web3 = useWeb3();

  // close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    if (showcalendar) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showcalendar]);

  // startDate/endDate params shared by the stats card and the transactions table
  const buildDateParams = useCallback(() => {
    const params = {};
    if (activeFilter === "date" && appliedDates) {
      params.startDate = appliedDates.start.toISOString();
      const end = new Date(appliedDates.end);
      end.setHours(23, 59, 59, 999);
      params.endDate = end.toISOString();
    } else if (activeFilter !== "date") {
      const { startDate, endDate } = getDateRange(activeFilter);
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
    }
    return params;
  }, [activeFilter, appliedDates]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = { privateSale, ...buildDateParams() };
      const res = await apiClient.get("/users/special/stats", { params });
      setStats(res.data.data.data);
    } catch (e) {
      console.error("Failed to fetch special round stats", e);
      // clear rather than leave the previous round's numbers on screen
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [buildDateParams, privateSale]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    setShowCalendar(false);
    setAppliedDates(null);
  };

  const handleDateRangeChange = (item) => {
    setDateRangeState([item.selection]);
    const { startDate, endDate } = item.selection;
    // close and apply once the user picks a full range (end differs from start)
    if (endDate && startDate.toDateString() !== endDate.toDateString()) {
      setAppliedDates({ start: startDate, end: endDate });
      setActiveFilter("date");
      setShowCalendar(false);
    }
  };
  // Reads the special-round contract: getRoundCount() -> uint32, then rounds(i)
  // for every index. Rounds are 1-indexed on-chain (index 0 is always empty),
  // so valid indexes are 1..roundCount. A round is "live" when now is inside
  // [startTime, endTime].
  const roundIsLiveHandler = useCallback(async () => {

    const contractAddrress = roundContract;
    if (!contractAddrress) {
      setChainRounds([]);
      setLiveRound(null);
      setRoundLoading(false);
      return null;
    }
    setRoundLoading(true);
    try {
      const contract = getPreSaleContract(contractAddrress, web3);

      const roundCount = Number(await contract.methods.getRoundCount().call());
      console.log(roundCount, 'contract', contractAddrress);
      if (!roundCount) {
        setChainRounds([]);
        setLiveRound(null);
        return null;
      }

      const raw = await Promise.all(
        Array.from({ length: roundCount }, (_, i) =>
          contract.methods.rounds(i + 1).call()
        )
      );
      console.log(raw, 'raw');

      const now = Math.floor(Date.now() / 1000);
      const parsed = raw
        .map((r, i) => {
          const startTime = Number(r.startTime);
          const endTime = Number(r.endTime);
          const isLive = now >= startTime && now <= endTime;
          return {
            index: i + 1,
            startTime,
            endTime,
            price: r.price, // 1e18-scaled, keep as string
            isLive,
            status: isLive ? "Live" : now < startTime ? "Upcoming" : "Ended",
          };
        })
        // skip slots that were never configured on-chain
        .filter((r) => r.startTime > 0 && r.endTime > 0);

      // live round first; otherwise the next one to start; otherwise the most
      // recently ended one — so the status card always has something to show.
      const live =
        parsed.find((r) => r.isLive) ||
        parsed
          .filter((r) => r.startTime > now)
          .sort((a, b) => a.startTime - b.startTime)[0] ||
        [...parsed].sort((a, b) => b.endTime - a.endTime)[0] ||
        null;

      setChainRounds(parsed);
      setLiveRound(live);
      return live;
    } catch (e) {
      console.error("Failed to read special round from contract", e);
      setChainRounds([]);
      setLiveRound(null);
      return null;
    } finally {
      setRoundLoading(false);
    }
  }, [web3, roundContract]);

  useEffect(() => {
    roundIsLiveHandler();
  }, [roundIsLiveHandler]);
  // recompute only when the filter / selected dates change — NOT on every render
  // (getDateRange uses `new Date()`, so calling it inline would produce fresh
  // timestamps each render and refetch the table on unrelated re-renders such as
  // toggling the calendar).
  const dateParams = useMemo(() => buildDateParams(), [buildDateParams]);

  return (
    <section className="kaispecialrounds">
      <div className="custom-container">
        <div className="topkairounds">
          <div className="leftheadings">
            <p className="premiumpara">Premium Projects</p>
            <h1 className="roundhead">KAI Special Rounds</h1>
          </div>
          <div className="maintime">
            {TIME_FILTERS.map((filter) => (
              <button
                key={filter}
                className={`innertime${activeFilter === filter ? " active" : ""}`}
                onClick={() => handleFilterClick(filter)}
              >
                {filter}
              </button>
            ))}

            <div className="cal" ref={calendarRef}>
              <button
                className={`innertime${activeFilter === "date" ? " active" : ""}`}
                onClick={() => setShowCalendar(!showcalendar)}
              >
                <CalendarIcon active={activeFilter === "date"} />
              </button>

              {showcalendar && (
                <div className="set-custom-calendar-div">
                  <DateRange
                    editableDateInputs={true}
                    onChange={handleDateRangeChange}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRangeState}
                    maxDate={new Date()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="roundtabs">
          {ROUNDS.map((item) => (
            <button
              key={item.key}
              className={`roundtab${activeRound === item.key ? " active" : ""}`}
              onClick={() => setActiveRound(item.key)}
            >
              {item.title}
            </button>
          ))}
        </div>

        <RoundStats
          stats={stats}
          loading={statsLoading}
          round={liveRound}
          roundLoading={roundLoading}
          roundMeta={activeRoundMeta}
        />

        <RoundTransactions
          startDate={dateParams.startDate}
          endDate={dateParams.endDate}
          roundMeta={activeRoundMeta}
        />
      </div>
    </section>
  );
};

export default KaiSpecialRounds;
